import { Alert, Box, TextField, Typography } from '@evaneos/b2b-ui';
import { ChangeEvent, FormEvent, useState } from 'react';
import { useIntl } from 'react-intl';

import { DisplayProject } from '../../../../resources/dossier-overview/types';
import { useUpdatePaxNumberMutation } from '../../../../resources/dossier/mutations';
import { DossierId } from '../../../../resources/dossier/types';
import { DefinitionList } from '../../../../design-system/DefinitionList/DefinitionList';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../../shared/analytics';

const isValidPaxNumber = (value: string | number | undefined | null): value is number => {
    return typeof value === 'number' && Number.isFinite(value) && value > 0;
};

type PaxNumberEditableItemProps = {
    project: DisplayProject | undefined;
    dossierId: DossierId;
    labelId: string;
    disabled: boolean;
};

export function PaxNumberEditableItem({
    project,
    dossierId,
    labelId,
    disabled,
}: PaxNumberEditableItemProps) {
    const { formatMessage } = useIntl();
    const [paxValue, setPaxValue] = useState<string | number>(project?.paxNumber ?? '');
    const [hasPaxError, setHasPaxError] = useState(false);
    const updatePaxNumberMutation = useUpdatePaxNumberMutation(dossierId);
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    const handlePaxNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { value, valueAsNumber } = e.currentTarget;
        const newValue = value === '' ? '' : valueAsNumber;
        setPaxValue(newValue);

        if (isValidPaxNumber(newValue)) {
            setHasPaxError(false);
        }
    };

    const handleBeforeClose = () => {
        if (!project) return true;

        const currentPaxValue = paxValue === '' ? project.paxNumber : paxValue;

        if (!isValidPaxNumber(currentPaxValue)) {
            setHasPaxError(true);
            return false;
        }

        if (currentPaxValue !== project.paxNumber && !updatePaxNumberMutation.isPending) {
            updatePaxNumberMutation.mutate({ paxNumber: currentPaxValue });

            trackEvent((ampli, defaultProperties) => {
                ampli.mainInformationChanged({
                    ...defaultProperties,
                    ...trackedTripProperties,
                    edited_field: 'nb pax',
                });
            });
        }

        setHasPaxError(false);
        return true;
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>, close: () => void) => {
        e.preventDefault();
        close();
    };

    return (
        <DefinitionList.EditableValue
            labelId={labelId}
            popoverWidth="trigger"
            placement="over"
            onBeforeClose={handleBeforeClose}
            disabled={disabled}
            disabledTooltip={
                disabled
                    ? formatMessage({ id: 'dossier.information.disabled.contract_tooltip' })
                    : undefined
            }
            editor={({ close }) => (
                <Box component="form" position="relative" onSubmit={(e) => handleSubmit(e, close)}>
                    <TextField
                        name="pax-number"
                        type="number"
                        error={hasPaxError}
                        slotProps={{
                            htmlInput: {
                                'data-testid': 'input-pax-number',
                                'aria-labelledby': labelId,
                                'aria-invalid': hasPaxError,
                                'aria-describedby': hasPaxError ? 'pax-error' : undefined,
                                min: 1,
                                sx: {
                                    fontSize: '1.4rem',
                                },
                            },
                        }}
                        size="small"
                        defaultValue={project?.paxNumber ?? ''}
                        onChange={handlePaxNumberChange}
                        fullWidth
                        autoFocus
                    />
                    {hasPaxError && (
                        <Alert
                            id="pax-error"
                            severity="error"
                            sx={{ position: 'absolute', top: '110%', left: 0, right: 0 }}
                        >
                            <Typography variant="body2" color="error.700">
                                {formatMessage({
                                    id: 'dossier.information.travellers.error.invalid_number',
                                })}
                            </Typography>
                        </Alert>
                    )}
                </Box>
            )}
        >
            {project?.paxNumber && (
                <Typography variant="body2" component="strong">
                    {formatMessage({ id: 'project.paxNumber' }, { pax: project?.paxNumber })}
                </Typography>
            )}
        </DefinitionList.EditableValue>
    );
}
